import React, { useState } from 'react';
import { Trans } from 'react-i18next';
import { Strong } from '../../../components/atoms/Strong/Strong';
import { Namespace } from '../../../translation/namespace';
import { Icons, SVGIcon } from '../../../components/atoms/SVG';
import { TooltipPlacement } from '../../../components/atoms/Tooltip';
import ModalDialog from '../../../components/organisms/ModalDialog/ModalDialog';
import { ModalDialogModifier } from '../../../components/organisms/ModalDialog/ModalDialog.modifier';
import YesNoForm from '../../../components/forms/DeleteForm/YesNoForm';
import Btn, { BtnModifier } from '../../../components/molecules/Btn';
import { TopHeadlinesForm } from '../TopHeadlinesForm/TopHeadlinesForm';
import { ContentHeadline } from '../../../_lib/components/ContentHeadline';
import IForm from '../../../types/IForm';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { TopHeadlineImage, TopHeadlineImageSmall } from '../../../types/api/ImageType';
import { useBlockContext } from '../../../_lib/BlockFetcher';
import { BlockType } from '../../../types/api/ElementType';
import { useWidgetContext } from '../../../_lib/components/Widget';

const INTERACTION = {
  EDIT: 'edit',
  DELETE: 'delete',
};

type TopHeadlineInteractions = {
  id: string;
  deleteForm?: IForm;
  editForm?: IForm;
  title: string;
  isCurrentlyVisible?: boolean;
  index: number;
  image: TopHeadlineImage | TopHeadlineImageSmall;
};

const getStateIndicatorStatus = (isCurrentlyVisible: undefined | boolean): undefined | 'green' | 'red' => {
  if (isCurrentlyVisible === undefined) {
    return isCurrentlyVisible;
  }
  if (isCurrentlyVisible) {
    return 'green';
  }
  return 'red';
};

const getStateIndicatorHelpText = (isCurrentlyVisible: undefined | boolean, t: any): undefined | string => {
  if (isCurrentlyVisible === undefined) {
    return isCurrentlyVisible;
  }
  if (isCurrentlyVisible) {
    return t('statusText.visible') as string;
  }
  return t('statusText.invisible') as string;
};

export default function TopHeadlineInteractions({
  title,
  editForm,
  deleteForm,
  isCurrentlyVisible,
  index,
  id,
  image,
}: TopHeadlineInteractions) {
  const { t } = useSonTranslation('TopHeadlines');
  const { elementType } = useBlockContext();
  const { widgetId } = useWidgetContext();
  const [activeInteraction, setActiveInteraction] = useState<'delete' | 'edit' | null>(null);

  const closeModal = () => setActiveInteraction(null);

  if (!deleteForm || !editForm) {
    return null;
  }

  const count = index + 1;

  const createButton = (interactionType: 'delete' | 'edit', sprite: SVGIcon) => (
    <div className="top-headlines__interactions-button">
      <Btn
        modifier={[BtnModifier.MINOR_ACTION, BtnModifier.TRANSPARENT]}
        text={t(`interactions.${interactionType}.titleButton`, { count })}
        titleText={t(`interactions.${interactionType}.titleButton`, { count })}
        hiddenText
        svgSpriteId={sprite}
        tooltipPlacement={TooltipPlacement.TOP}
        onClick={() => setActiveInteraction(interactionType)}
      />
    </div>
  );

  const getDeleteQuestion = () => (
    <Trans i18nKey={`${Namespace.TopHeadlines}:interactions.delete.question`}>
      <Strong>{title}</Strong>
    </Trans>
  );

  return (
    <div className="top-headlines__interactions-bar" key={`top-headline-interactions-${index}`}>
      <div className="top-headlines__interactions-info">
        <ContentHeadline
          headingId={id}
          // archive begins with <h1> / regular overview begins with <h2>
          hierarchy={elementType === BlockType.topheadlinesarchive ? 2 : 3}
          title={`${t('topHeadline')} ${index + 1}`}
          modifier="no-margin-bottom"
          stateIndicatorStatus={getStateIndicatorStatus(isCurrentlyVisible)}
          stateIndicatorHelpText={getStateIndicatorHelpText(isCurrentlyVisible, t)}
        />
      </div>
      <div className="top-headlines__interactions-buttons">
        {createButton('edit', Icons.EDIT_PENCIL)}
        {createButton('delete', Icons.DELETE_TRASHCAN)}
      </div>
      {activeInteraction !== null && (
        <ModalDialog
          closeModal={closeModal}
          headline={t(`interactions.${activeInteraction}.title`)}
          initialFocusIndex={activeInteraction === INTERACTION.DELETE ? 1 : 2}
          modifier={activeInteraction === INTERACTION.DELETE ? null : ModalDialogModifier.WITH_NAVIGATION}
        >
          {activeInteraction === INTERACTION.DELETE && (
            <YesNoForm
              id="top-headline-delete-form"
              form={deleteForm}
              validateResponseStatus={statusNumber => statusNumber === 200}
              transNamespaceAndKey={`${Namespace.TopHeadlines}:interactions.delete`}
              headline={getDeleteQuestion()}
              cancelAction={closeModal}
              widgetId={widgetId}
            />
          )}
          {activeInteraction === INTERACTION.EDIT && <TopHeadlinesForm form={editForm} type="update" image={image} />}
        </ModalDialog>
      )}
    </div>
  );
}
